import { createApp } from 'vue'

import Navigation from './components/Navigation.vue'
import Accordion from './components/Accordion.vue'
import AccordionItem from './components/AccordionItem.vue'
import Search from './components/Search.vue'
import BeforeAfter from './components/BeforeAfter.vue'
import VideoConsent from './components/VideoConsent.vue'
import AnalyticsConsent from './components/AnalyticsConsent.vue'
import CountUp from './components/CountUp.vue'
import StepByStep from './components/StepByStep.vue'
import ProjectFilter from './components/ProjectFilter.vue'
import TeamFilter from './components/TeamFilter.vue'
import { initSwiperSliders } from './swiper-slider.js'
import { initLightbox } from './lightbox.js'
import { initProjectMosaics } from './mosaic.js'

// data-Attribute mit JSON-Inhalt tolerant parsen (leer/kaputt → {})
function safeJsonParse(value) {
	try {
		return JSON.parse(value || '{}')
	} catch {
		return {}
	}
}

// Vue-Komponenten je CSS-Selektor. props(el) liest die Props aus
// data-Attributen des Mount-Elements; mount(el) erlaubt Sonderfälle
// (z.B. das Kompilieren von server-gerendertem Markup als Template).
const vueRegistry = [
	{
		selector: '#navigation-vue',
		name: 'Navigation',
		component: Navigation,
		props: (el) => ({
			navigationItems: el.getAttribute('data-navigation'),
			logo: el.getAttribute('data-logo'),
			menuLabel: el.getAttribute('data-menu-label') || 'Menü',
		}),
	},
	{
		// Server-gerendertes <accordion>/<accordion-item>-Markup als Template
		// kompilieren — ein normaler Komponenten-Mount würde die Slots verwerfen.
		selector: '[data-vue-accordion]',
		name: 'Accordion',
		mount: (el) => {
			const app = createApp({
				template: el.innerHTML,
				compilerOptions: { whitespace: 'preserve' },
			})
			app.component('Accordion', Accordion)
			app.component('AccordionItem', AccordionItem)
			el._vueApp = app
			return app.mount(el)
		},
	},
	{
		selector: '[data-vue-before-after]',
		name: 'BeforeAfter',
		component: BeforeAfter,
		lazy: true,
		props: (el) => ({
			beforeSrc: el.getAttribute('data-before-src'),
			afterSrc: el.getAttribute('data-after-src'),
			beforeLabel: el.getAttribute('data-before-label') || '',
			afterLabel: el.getAttribute('data-after-label') || '',
			beforeAlt: el.getAttribute('data-before-alt') || '',
			afterAlt: el.getAttribute('data-after-alt') || '',
		}),
	},
	{
		selector: '[data-vue-video-consent]',
		name: 'VideoConsent',
		component: VideoConsent,
		props: (el) => ({
			videoUrl: el.getAttribute('data-video-url') || el.getAttribute('data-iframe-src') || '',
			provider: el.getAttribute('data-provider') || '',
			posterSrc: el.getAttribute('data-poster-src') || '',
			title: el.getAttribute('data-title') || '',
			privacyUrl: el.getAttribute('data-privacy-url') || '',
		}),
	},
	{
		// Einwilligung NUR für die Reichweitenmessung. Externe Einbettungen
		// fragen separat am Element (VideoConsent) — siehe consent.js.
		selector: '[data-vue-analytics-consent]',
		name: 'AnalyticsConsent',
		component: AnalyticsConsent,
		props: (el) => ({
			matomoUrl: el.getAttribute('data-matomo-url') || '',
			matomoSiteId: el.getAttribute('data-matomo-site-id') || '',
			privacyUrl: el.getAttribute('data-privacy-url') || '',
			privacyLabel: el.getAttribute('data-privacy-label') || undefined,
		}),
	},
	{
		selector: '[data-vue-countup]',
		name: 'CountUp',
		component: CountUp,
		lazy: true,
		props: (el) => ({
			target: parseInt(el.getAttribute('data-target') || '0', 10),
			duration: parseInt(el.getAttribute('data-duration') || '2500', 10),
			prefix: el.getAttribute('data-prefix') || '',
			suffix: el.getAttribute('data-suffix') || '',
			grouping: el.getAttribute('data-grouping') !== 'false',
		}),
	},
	{
		selector: '[data-vue-steps]',
		name: 'StepByStep',
		component: StepByStep,
	},
	{
		// Filterleiste für das Projekt-Mosaik. Mountet auf den (leeren) Platzhalter
		// im ProjectGrid; die Komponente liest Kategorien/Tags aus den
		// server-gerenderten Kacheln und blendet nicht passende aus.
		selector: '[data-vue-project-filter]',
		name: 'ProjectFilter',
		component: ProjectFilter,
	},
	{
		// Filterleiste für das Team-Mosaik. Baut Personen-/Rollen-Filter aus den
		// data-Attributen der server-gerenderten Kacheln.
		selector: '[data-vue-team-filter]',
		name: 'TeamFilter',
		component: TeamFilter,
	},
	{
		selector: '#search-vue',
		name: 'Search',
		component: Search,
		props: (el) => ({
			placeholder: el.getAttribute('data-placeholder') || 'Suche...',
			noResultsMessage: el.getAttribute('data-no-results-message') || 'Keine Ergebnisse gefunden.',
			searchTerm: el.getAttribute('data-search-term') || '',
			resultsPerPage: parseInt(el.getAttribute('data-results-per-page') || '10', 10),
			endpoint: el.getAttribute('data-endpoint') || '/api/search',
			labels: safeJsonParse(el.getAttribute('data-labels')),
		}),
	},
]

// Nicht-Vue-Enhancer für server-gerendertes Markup (idempotent!)
const enhancers = [initSwiperSliders, initLightbox, initProjectMosaics]

// Cross-Origin-sicher: in fremden iframes wirft der Zugriff auf window.parent.Neos
// einen SecurityError und würde ohne try/catch sämtliche Mounts verhindern.
function detectNeosBackend() {
	try {
		return window.parent !== window && !!window.parent.Neos
	} catch {
		return false
	}
}

function mountVueComponents() {
	const isNeosBackend = detectNeosBackend()

	const mountElement = (entry, element) => {
		if (element._vueInstance) return
		if (entry.mount) {
			element._vueInstance = entry.mount(element)
		} else {
			const app = createApp(entry.component, entry.props ? entry.props(element) : undefined)
			// App-Referenz aufheben, damit re-gerenderte Inhalte sauber
			// per element._vueApp.unmount() abgeräumt werden können.
			element._vueApp = app
			element._vueInstance = app.mount(element)
		}
		if (isNeosBackend) {
			document.dispatchEvent(new CustomEvent('vue:mounted', { detail: { component: entry.name } }))
		}
	}

	vueRegistry.forEach((entry) => {
		document.querySelectorAll(entry.selector).forEach((element) => {
			if (element._vueInstance) return
			// Lazy components mount only once they approach the viewport (not in the
			// Neos backend, where eager mounting keeps inline editing predictable).
			if (entry.lazy && !isNeosBackend && 'IntersectionObserver' in window) {
				if (element._vueObserving) return
				element._vueObserving = true
				const observer = new IntersectionObserver((entries, obs) => {
					entries.forEach((e) => {
						if (e.isIntersecting) {
							obs.disconnect()
							mountElement(entry, e.target)
						}
					})
				}, { rootMargin: '200px' })
				observer.observe(element)
			} else {
				mountElement(entry, element)
			}
		})
	})

	enhancers.forEach((enhance) => enhance())
}

function setupMutationObserver() {
	// Mutationen pro Frame bündeln — ohne Debounce liefe der komplette
	// Mount-Sweep synchron bei jeder einzelnen DOM-Änderung (teuer beim
	// Inline-Editing im Backend).
	let scheduled = false
	const observer = new MutationObserver(() => {
		if (scheduled) return
		scheduled = true
		requestAnimationFrame(() => {
			scheduled = false
			mountVueComponents()
		})
	})

	observer.observe(document.body, {
		childList: true,
		subtree: true,
	})
}

document.addEventListener('DOMContentLoaded', () => {
	// Kontrast-Umschalter (Header-Button) — bewusst ohne Vue: die ganze Seite
	// als Vue-App zu mounten würde redaktionelle Inhalte mit '{{' oder
	// v-/@-Attributen als Template kompilieren und das Rendering brechen.
	window.toggleGrayscale = () => document.body.classList.toggle('grayscale-mode')

	mountVueComponents()
	setupMutationObserver()

	// Smooth scrolling für Anker-Links
	document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
		anchor.addEventListener('click', function (e) {
			const href = this.getAttribute('href')
			// href="#" ist kein gültiger Selektor (querySelector('#') wirft)
			if (!href || href === '#') return
			let target = null
			try {
				target = document.querySelector(href)
			} catch {
				return
			}
			if (target) {
				e.preventDefault()
				target.scrollIntoView({ behavior: 'smooth' })
				history.pushState(null, '', href)
			}
		})
	})
})

window.addEventListener('load', () => {
	mountVueComponents()
})
